/**
 * Vendors page — section components.
 *
 * Each visual section of the public Vendors page rendered as its own
 * focused component. The page file (`Vendors.jsx`) is the composition
 * shell that arranges these in order.
 */
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Section, SectionHeading, FadeIn, Stagger, StaggerItem, Eyebrow,
} from "@/components/Section";
import {
  ArrowRight, AlertTriangle, CheckCircle2, X, Download,
} from "lucide-react";
import NoderaMark from "@/components/NoderaMark";
import {
  VENDOR_PROBLEMS,
  SOLUTION_NODERA_PROVIDES,
  SOLUTION_VENDOR_OUTCOMES,
  HOW_IT_WORKS_STEPS,
  VALUE_PILLARS,
  VENDOR_PROFILES,
  SELECTION_FILTERS,
  ECONOMICS_BENEFITS,
  ECONOMICS_DISCLAIMERS,
  BOUNDARIES,
  GETTING_STARTED_STEPS,
  NETWORK_DIAGRAM_VENDORS,
  NETWORK_DIAGRAM_CLINICS,
  NETWORK_DIAGRAM_TRUST_CHIPS,
} from "./data";

// ---------------------------------------------------------------------------
// 1. Hero + network diagram
// ---------------------------------------------------------------------------
export function VendorHero() {
  return (
    <section data-testid="vendors-hero" className="relative overflow-hidden">
      <span className="hero-blob bg-blue-200 top-[-120px] left-[-120px] w-[420px] h-[420px]" />
      <span className="hero-blob bg-indigo-100 top-[15%] right-[-160px] w-[420px] h-[420px] opacity-60" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20 lg:pt-36 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-3.5 py-1.5 text-xs font-medium text-slate-700 shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-600 pulse-dot" /> For vendors
            </div>
            <h1 className="mt-6 font-display font-extrabold tracking-tighter text-slate-900 text-4xl sm:text-5xl lg:text-[60px] leading-[1.04]">
              Scale distribution into{" "}
              <span className="bg-gradient-to-r from-slate-900 via-blue-700 to-slate-900 bg-clip-text text-transparent">
                high-performing clinics
              </span>
              .
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              Join the Nodera network and connect your services to clinics deploying structured cash-pay programs through operational infrastructure and workflow automation.
            </p>
            <p className="mt-4 text-sm text-slate-500 leading-relaxed max-w-xl">
              Access qualified clinics, standardized deployment systems, and a platform built to support recurring utilization — without building your own sales or operational layer.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/vendors/apply"
                data-testid="vendor-hero-apply-btn"
                className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-6 py-3.5 text-sm font-semibold hover:bg-slate-800 transition-colors shadow-sm"
              >
                Apply to Join the Nodera Vendor Network <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="vendor-hero-partnerships-btn"
                className="inline-flex items-center gap-1.5 bg-white text-slate-900 border border-slate-200 rounded-full px-6 py-3.5 text-sm font-semibold hover:bg-slate-50 transition-colors"
              >
                Speak With Partnerships
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6">
            <NetworkDiagram />
          </div>
        </div>
      </div>
    </section>
  );
}

function NetworkDiagram() {
  return (
    <FadeIn>
      <div className="relative">
        <div className="absolute -inset-8 bg-gradient-to-tr from-blue-200/40 via-white to-slate-100/0 rounded-[2rem] blur-2xl" />
        <div className="relative rounded-2xl border border-slate-200 bg-white shadow-[0_24px_60px_-20px_rgba(15,23,42,0.25)] p-6 lg:p-8">
          <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400">
            <span>Network topology</span>
            <span className="inline-flex items-center gap-1.5 text-emerald-600">
              <span className="relative inline-flex w-1.5 h-1.5">
                <span className="absolute inset-0 rounded-full bg-emerald-500 pulse-dot" />
              </span>
              Live
            </span>
          </div>

          <div className="mt-8 grid grid-cols-[1fr_64px_1fr] sm:grid-cols-3 gap-2 sm:gap-4 items-center">
            <div className="space-y-2.5">
              {NETWORK_DIAGRAM_VENDORS.map((v, i) => (
                <motion.div
                  key={v}
                  initial={{ opacity: 0, x: -8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="rounded-lg bg-slate-50 border border-slate-200 px-2.5 py-2 text-[11px] sm:text-xs font-semibold text-slate-700 flex items-center gap-1.5 min-h-[52px]"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-600 shrink-0" />
                  <span className="leading-tight">{v}</span>
                </motion.div>
              ))}
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400 pt-1 text-center">
                Vendors
              </div>
            </div>

            <div className="flex flex-col items-center">
              <svg viewBox="0 0 100 200" className="w-full h-32" aria-hidden>
                {[40, 80, 120, 160].map((y) => (
                  <line key={`l${y}`} x1="0" x2="50" y1={y} y2="100" stroke="#cbd5e1" strokeWidth="1.4" />
                ))}
                {[40, 80, 120, 160].map((y) => (
                  <line key={`r${y}`} x1="50" x2="100" y1="100" y2={y} stroke="#cbd5e1" strokeWidth="1.4" />
                ))}
              </svg>
              <div className="mt-[-110px] w-16 h-16 sm:w-24 sm:h-24 rounded-2xl bg-slate-900 text-white flex flex-col items-center justify-center shadow-xl">
                <NoderaMark size={28} color="white" />
                <span className="text-[8px] sm:text-[10px] mt-0.5 tracking-[0.14em] uppercase text-blue-300 font-semibold">
                  Platform
                </span>
              </div>
              <div className="mt-3 text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">Nodera</div>
            </div>

            <div className="space-y-2.5">
              {NETWORK_DIAGRAM_CLINICS.map((v, i) => (
                <motion.div
                  key={v}
                  initial={{ opacity: 0, x: 8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="rounded-lg bg-slate-50 border border-slate-200 px-2.5 py-2 text-[11px] sm:text-xs font-semibold text-slate-700 flex items-center gap-1.5 min-h-[52px]"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                  <span className="leading-tight">{v}</span>
                </motion.div>
              ))}
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400 pt-1 text-center">
                Clinics
              </div>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-2 text-[11px] text-slate-500">
            {NETWORK_DIAGRAM_TRUST_CHIPS.map((c) => (
              <div
                key={c.v}
                className="rounded-lg bg-slate-50 border border-slate-100 px-2 py-2 min-h-[58px] flex flex-col justify-center text-center"
              >
                <div className="font-display font-bold text-slate-900 text-xs sm:text-sm tabular leading-tight">{c.v}</div>
                <div className="text-[8.5px] sm:text-[10px] uppercase tracking-normal sm:tracking-[0.14em] font-semibold text-slate-400 mt-0.5 leading-tight">{c.k}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </FadeIn>
  );
}

// ---------------------------------------------------------------------------
// 2. Problem
// ---------------------------------------------------------------------------
export function VendorProblemSection() {
  return (
    <Section className="bg-white" testId="vendors-problem">
      <SectionHeading
        eyebrow="The vendor problem"
        title="Great services don't scale without the right infrastructure."
        intro="Most vendors aren't limited by product. They're limited by distribution and operational consistency."
      />

      <Stagger stagger={0.05}>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {VENDOR_PROBLEMS.map((p, i) => (
            <StaggerItem key={p}>
              <div
                data-testid={`vendor-problem-${i}`}
                className="h-full rounded-2xl border border-slate-200 bg-slate-50/60 p-5 flex items-start gap-3"
              >
                <span className="w-8 h-8 rounded-lg bg-amber-50 border border-amber-100 text-amber-600 flex items-center justify-center shrink-0">
                  <X className="w-4 h-4" />
                </span>
                <span className="text-sm text-slate-700">{p}</span>
              </div>
            </StaggerItem>
          ))}
        </div>
      </Stagger>

      <FadeIn delay={0.15}>
        <div className="mt-10 rounded-2xl border border-slate-900 bg-slate-900 text-white p-6 lg:p-8">
          <p className="font-display text-lg sm:text-xl font-semibold leading-snug">
            Most vendors are not limited by product — they are limited by distribution and operational consistency.
          </p>
        </div>
      </FadeIn>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 3. Solution
// ---------------------------------------------------------------------------
export function VendorSolutionSection() {
  return (
    <Section className="bg-slate-50 border-y border-slate-200" testId="vendors-solution">
      <SectionHeading
        eyebrow="The infrastructure layer"
        title="An infrastructure layer for scalable clinic distribution."
        intro="Nodera enables vendors to integrate into a structured clinic ecosystem where services are packaged, deployed, supported, and aligned with recurring utilization."
      />

      <div className="mt-14 grid lg:grid-cols-2 gap-6">
        <FadeIn>
          <div className="rounded-2xl border border-slate-200 bg-white p-7 h-full">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-600">
              What Nodera provides
            </div>
            <ul className="mt-5 space-y-3.5">
              {SOLUTION_NODERA_PROVIDES.map((s) => (
                <li key={s} className="flex items-start gap-3">
                  <span className="mt-0.5 w-6 h-6 rounded-md bg-blue-600 text-white flex items-center justify-center shrink-0">
                    <CheckCircle2 className="w-3 h-3" />
                  </span>
                  <span className="text-sm text-slate-700">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </FadeIn>

        <FadeIn delay={0.08}>
          <div className="rounded-2xl border border-slate-900 bg-slate-900 text-white p-7 h-full">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-300">
              Vendor outcomes
            </div>
            <ul className="mt-5 space-y-3.5">
              {SOLUTION_VENDOR_OUTCOMES.map((row) => (
                <li key={row.t} className="flex items-start gap-3">
                  <span className="mt-0.5 w-7 h-7 rounded-md bg-white/10 border border-white/15 text-white flex items-center justify-center shrink-0">
                    <row.icon className="w-3.5 h-3.5" />
                  </span>
                  <span className="text-sm text-slate-100">{row.t}</span>
                </li>
              ))}
            </ul>
          </div>
        </FadeIn>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 4. How it works
// ---------------------------------------------------------------------------
export function VendorHowItWorksSection() {
  return (
    <Section className="bg-white" testId="vendors-how">
      <SectionHeading eyebrow="Network integration" title="How vendors integrate into the Nodera network." />

      <div className="mt-14 grid lg:grid-cols-5 gap-4 relative">
        <div className="hidden lg:block absolute top-[38px] left-[8%] right-[8%] h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
        {HOW_IT_WORKS_STEPS.map((s, i) => (
          <FadeIn key={s.n} delay={i * 0.06}>
            <div
              data-testid={`vendor-step-${i + 1}`}
              className="relative h-full rounded-2xl border border-slate-200 bg-white p-5"
            >
              <div className="relative w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center font-display font-bold">
                {s.n}
              </div>
              <div className="mt-4 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-600">
                <s.icon className="w-3.5 h-3.5" /> Step {i + 1}
              </div>
              <h3 className="mt-1.5 font-display text-base font-semibold text-slate-900 leading-snug">{s.title}</h3>
              <ul className="mt-3 space-y-1.5 text-xs text-slate-600">
                {s.items.map((it) => (
                  <li key={it} className="flex items-start gap-1.5">
                    <span className="mt-1.5 w-1 h-1 rounded-full bg-slate-400" />
                    {it}
                  </li>
                ))}
              </ul>
            </div>
          </FadeIn>
        ))}
      </div>

      <FadeIn delay={0.2}>
        <div className="mt-10 rounded-2xl border border-amber-200 bg-amber-50/60 p-6 flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-sm text-slate-700 leading-relaxed">
            Nodera does not direct clinical decision-making or control patient care. Vendors operate independently and are selected based on their ability to support compliant, scalable service delivery.
          </p>
        </div>
      </FadeIn>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 5. Value pillars
// ---------------------------------------------------------------------------
export function VendorValuePillarsSection() {
  return (
    <Section className="bg-slate-50 border-y border-slate-200" testId="vendors-value">
      <SectionHeading eyebrow="Value pillars" title="Why vendors join Nodera." />

      <Stagger stagger={0.06}>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {VALUE_PILLARS.map((v, i) => (
            <StaggerItem key={v.title}>
              <div
                data-testid={`vendor-pillar-${i}`}
                className="h-full rounded-2xl border border-slate-200 bg-white p-6 hover:border-blue-200 hover:shadow-sm transition-all"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <v.icon className="w-4.5 h-4.5" strokeWidth={1.8} />
                </div>
                <h3 className="mt-5 font-display text-base font-semibold text-slate-900">{v.title}</h3>
                <ul className="mt-3 space-y-1.5 text-sm text-slate-600">
                  {v.items.map((it) => (
                    <li key={it} className="flex items-start gap-1.5">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-slate-400" />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            </StaggerItem>
          ))}
        </div>
      </Stagger>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 6. Ideal vendor profiles
// ---------------------------------------------------------------------------
export function VendorProfilesSection() {
  return (
    <Section className="bg-white" testId="vendors-profiles">
      <SectionHeading eyebrow="Who the network is built for" title="Vendor categories we partner with." />

      <Stagger stagger={0.04}>
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {VENDOR_PROFILES.map((c, i) => (
            <StaggerItem key={c.label}>
              <div
                data-testid={`vendor-profile-${i}`}
                className="h-full rounded-2xl border border-slate-200 bg-white p-5"
              >
                <span className="w-9 h-9 rounded-lg bg-slate-900 text-white flex items-center justify-center">
                  <c.icon className="w-4 h-4" />
                </span>
                <div className="mt-4 font-display text-sm font-semibold text-slate-900 leading-snug">{c.label}</div>
              </div>
            </StaggerItem>
          ))}
        </div>
      </Stagger>

      <FadeIn delay={0.2}>
        <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50/60 p-6 lg:p-8">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-600 mb-3">
            Selection filter
          </div>
          <p className="font-display text-base sm:text-lg font-semibold text-slate-900 leading-snug">
            We partner with vendors that:
          </p>
          <ul className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-slate-700">
            {SELECTION_FILTERS.map((t) => (
              <li key={t} className="flex items-start gap-2">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-slate-900" />
                {t}
              </li>
            ))}
          </ul>
        </div>
      </FadeIn>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 7. Economics
// ---------------------------------------------------------------------------
export function VendorEconomicsSection() {
  return (
    <Section className="bg-slate-50 border-y border-slate-200" testId="vendors-network-economics">
      <div className="grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-6">
          <SectionHeading
            eyebrow="Aligned with clinic economics"
            title="Highly competitive contracted rates — passed straight through."
            intro="Nodera negotiates wholesale rates across the vendor network so each Nodera clinic gets enterprise-grade pricing on services they'd otherwise source one-off. Vendor–clinic relationships stay direct; Nodera operates underneath."
          />
        </div>
        <div className="lg:col-span-6 space-y-4">
          <FadeIn>
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <ul className="space-y-3 text-sm text-slate-700">
                {ECONOMICS_BENEFITS.map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <span className="mt-0.5 w-5 h-5 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-3 h-3" />
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </FadeIn>

          <FadeIn delay={0.08}>
            <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-6">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-amber-700">
                <AlertTriangle className="w-3.5 h-3.5" /> Important
              </div>
              <ul className="mt-3 space-y-2 text-sm text-slate-700">
                {ECONOMICS_DISCLAIMERS.map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <span className="mt-1.5 w-1 h-1 rounded-full bg-amber-600" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </FadeIn>
        </div>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 8. Boundaries (trust builder)
// ---------------------------------------------------------------------------
export function VendorBoundariesSection() {
  return (
    <Section className="bg-white" testId="vendors-boundaries">
      <SectionHeading
        eyebrow="Clear boundaries"
        title="Clear boundaries. Clean structure."
        intro="A precise scope is what makes Nodera a safe and durable channel for vendors to scale through."
      />

      <div className="mt-14 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7">
          <Stagger stagger={0.04}>
            <ul className="grid sm:grid-cols-2 gap-3">
              {BOUNDARIES.map((t, i) => (
                <StaggerItem key={t}>
                  <li
                    data-testid={`vendor-boundary-${i}`}
                    className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 flex items-start gap-2.5"
                  >
                    <span className="mt-0.5 w-5 h-5 rounded-md bg-slate-200 text-slate-700 flex items-center justify-center shrink-0">
                      <X className="w-3 h-3" />
                    </span>
                    <span className="text-sm text-slate-700">{t}</span>
                  </li>
                </StaggerItem>
              ))}
            </ul>
          </Stagger>
        </div>

        <FadeIn delay={0.1} className="lg:col-span-5">
          <div className="rounded-2xl border border-slate-900 bg-slate-900 text-white p-6 lg:p-7 h-full">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-300">
              Why this matters
            </div>
            <p className="mt-3 font-display text-lg font-semibold leading-snug">
              This structure allows vendors to scale while maintaining independence and compliance.
            </p>
            <p className="mt-3 text-sm text-slate-300 leading-relaxed">
              You stay in control of your services, regulatory posture, and customer relationships. We handle the operational layer that turns those services into deployable programs across qualified clinics.
            </p>
          </div>
        </FadeIn>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 9. Getting started
// ---------------------------------------------------------------------------
export function VendorGettingStartedSection() {
  return (
    <Section className="bg-slate-50 border-y border-slate-200" testId="vendors-getting-started">
      <SectionHeading
        eyebrow="Get started"
        title="Join the network."
        intro="A structured five-step process from application to live deployment."
      />

      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {GETTING_STARTED_STEPS.map((s, i) => (
          <FadeIn key={s.n} delay={i * 0.05}>
            <div
              data-testid={`vendor-getting-started-${i + 1}`}
              className="h-full rounded-2xl border border-slate-200 bg-white p-5"
            >
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-600">
                Step {i + 1}
              </div>
              <div className="mt-2 flex items-center gap-2.5">
                <span className="w-9 h-9 rounded-lg bg-slate-900 text-white flex items-center justify-center">
                  <s.icon className="w-4 h-4" />
                </span>
                <div className="font-display font-semibold text-slate-900">{s.t}</div>
              </div>
            </div>
          </FadeIn>
        ))}
      </div>

      <div className="mt-10 text-center">
        <Link
          to="/vendors/apply"
          data-testid="vendor-getting-started-apply-btn"
          className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-6 py-3.5 text-sm font-semibold hover:bg-slate-800 transition-colors"
        >
          Apply to Join the Nodera Vendor Network <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 10. Final CTA
// ---------------------------------------------------------------------------
export function VendorFinalCtaSection() {
  return (
    <Section className="bg-white" testId="vendors-final-cta">
      <FadeIn>
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950 text-white p-10 lg:p-16">
          <span className="absolute -top-40 -right-20 w-[500px] h-[500px] rounded-full bg-blue-500/20 blur-3xl" />

          <div className="relative grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <Eyebrow className="text-blue-300">Final step</Eyebrow>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
                Expand into clinics that are built to use what you offer.
              </h2>
              <p className="mt-5 text-slate-300 max-w-2xl leading-relaxed">
                Join a network designed to turn services into scalable programs through operational infrastructure.
              </p>
            </div>
            <div className="lg:col-span-4 lg:flex lg:flex-col lg:items-end gap-3">
              <Link
                to="/vendors/apply"
                data-testid="vendor-final-cta-btn"
                className="inline-flex items-center gap-2 bg-white text-slate-900 rounded-full px-6 py-3.5 text-sm font-semibold hover:bg-slate-100 transition-colors"
              >
                Apply to Join the Nodera Vendor Network <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="/flyers/vendor-network.pdf"
                download
                data-testid="vendor-final-cta-flyer-download"
                className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white rounded-full px-5 py-2.5 text-xs font-semibold hover:bg-white/15 transition-colors"
              >
                <Download className="w-3.5 h-3.5" /> Download launch flyer (PDF)
              </a>
            </div>
          </div>
        </div>
      </FadeIn>

      <p className="mt-10 text-xs text-slate-500 max-w-3xl mx-auto text-center leading-relaxed">
        Nodera is a software and operational infrastructure platform. Clinical services are provided by licensed healthcare providers. Vendors maintain independent compliance and service delivery responsibilities.
      </p>
    </Section>
  );
}
